<template>
  <LayoutWrapper>
    <div class="mx-auto mt-24 md:mt-10 max-w-6xl px-4 md:px-8">
      <!-- Header -->
      <div class="mb-6">
        <h1 class="text-2xl md:text-3xl font-semibold tracking-tight">
          Sandbox
        </h1>
        <p class="mt-1 text-sm text-neutral-600 dark:text-neutral-300">
          Dev playground for quickly starting an order. Paste JSON or choose a
          type.
        </p>
      </div>

      <div class="grid grid-cols-1 gap-4 lg:gap-6 md:grid-cols-2">
        <div
          class="rounded-xl border border-light-2 bg-light-0 p-4 md:p-5 shadow-sm dark:border-dark-2 dark:bg-dark-0"
        >
          <div class="flex items-start justify-between">
            <div>
              <div class="text-lg font-semibold">Order Input</div>
              <p class="mt-1 text-xs text-neutral-600 dark:text-neutral-300">
                Choose an order type or paste an order JSON. If provided, JSON
                will override the type.
              </p>
            </div>
          </div>

          <div class="mt-4 space-y-4">
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium">Order Type</label>
              <select
                v-model="selectedOrderType"
                class="w-full rounded-lg border border-light-2 bg-light-0 p-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-2/40 focus:border-primary-2 dark:border-dark-2 dark:bg-dark-0"
              >
                <option
                  v-for="opt in orderTypeOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>
            </div>

            <div class="flex flex-col">
              <div class="flex items-center justify-between">
                <label class="text-sm font-medium">Order JSON (optional)</label>
                <span class="text-[11px] text-neutral-500 dark:text-neutral-400"
                  >Paste raw JSON</span
                >
              </div>
              <textarea
                v-model="orderJsonInput"
                class="w-full rounded-lg border border-light-2 bg-light-0 p-3 font-mono text-xs leading-5 shadow-sm resize-y min-h-48 focus:outline-none focus:ring-2 focus:ring-primary-2/40 focus:border-primary-2 dark:border-dark-2 dark:bg-dark-0"
                placeholder='{"id": "...", "orderId": "...", "appointment": {"start": "...", "end": "..."}}'
                rows="12"
              />
              <div
                class="mt-2 flex flex-col items-center justify-center border-2 border-dashed border-neutral-300 dark:border-neutral-600 rounded-lg p-3 text-xs text-neutral-500 hover:bg-light-1 dark:hover:bg-dark-1 transition cursor-pointer"
                @drop.prevent="handleFileDrop($event, 'order')"
                @dragover.prevent
                @click="triggerFileSelect('order')"
              >
                <input
                  ref="orderFileInput"
                  type="file"
                  accept=".json"
                  class="hidden"
                  @change="handleFileSelectChange($event, 'order')"
                />
                <span>Drop Order JSON here or click to upload</span>
              </div>
              <span
                v-if="orderJsonError"
                class="mt-1 text-xs text-light-error dark:text-dark-error"
              >
                {{ orderJsonError }}
              </span>
              <span class="mt-1 text-xs text-neutral-600 dark:text-neutral-400">
                If provided, Order JSON takes precedence over Order Type.
              </span>
            </div>
          </div>
        </div>

        <div
          class="rounded-xl border border-light-2 bg-light-0 p-4 md:p-5 shadow-sm dark:border-dark-2 dark:bg-dark-0"
        >
          <div class="flex items-center justify-between mb-2 gap-2">
            <div class="flex items-center gap-2">
              <input
                v-model="propertyIdSearch"
                type="text"
                placeholder="Enter Property ID"
                class="rounded-lg border border-light-2 bg-light-0 p-2 text-xs dark:border-dark-2 dark:bg-dark-0"
              />
              <button
                class="rounded-lg bg-primary-2 px-3 py-1.5 text-white text-xs font-medium shadow hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-primary-2/40"
                @click="fetchBusinessEntityVersions"
              >
                Search Versions
              </button>
            </div>
          </div>
          <div class="text-lg font-semibold">Business Entity JSON</div>
          <p class="mt-1 text-xs text-neutral-600 dark:text-neutral-300">
            Required. Used to populate IDs and addresses on the order.
          </p>

          <div class="mt-4 flex flex-col gap-1">
            <label class="text-sm font-medium sr-only"
              >Business Entity JSON</label
            >
            <textarea
              v-model="advancedInput"
              class="w-full rounded-lg border border-light-2 bg-light-0 p-3 font-mono text-xs leading-5 shadow-sm resize-y min-h-64 md:min-h-72 focus:outline-none focus:ring-2 focus:ring-primary-2/40 focus:border-primary-2 dark:border-dark-2 dark:bg-dark-0"
              placeholder='{"businessEntityId": "...", "propertyId": "..."}'
              rows="14"
            />
            <div
              class="mt-2 flex flex-col items-center justify-center border-2 border-dashed border-neutral-300 dark:border-neutral-600 rounded-lg p-3 text-xs text-neutral-500 hover:bg-light-1 dark:hover:bg-dark-1 transition cursor-pointer"
              @drop.prevent="handleFileDrop($event, 'be')"
              @dragover.prevent
              @click="triggerFileSelect('be')"
            >
              <input
                ref="beFileInput"
                type="file"
                accept=".json"
                class="hidden"
                @change="handleFileSelectChange($event, 'be')"
              />
              <span>Drop Business Entity JSON here or click to upload</span>
            </div>
            <span
              v-if="beJsonError"
              class="mt-1 text-xs text-light-error dark:text-dark-error"
            >
              {{ beJsonError }}
            </span>
          </div>
        </div>
      </div>

      <div
        class="mt-4 rounded-xl border border-light-2 bg-light-0 p-4 md:p-5 shadow-sm dark:border-dark-2 dark:bg-dark-0"
      >
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 class="text-lg font-semibold">Cell Signal Plugin Test</h2>
            <p class="mt-1 text-xs text-neutral-600 dark:text-neutral-300">
              Starts <code class="font-mono">CellSignal.startMonitoring()</code>
              and listens for
              <code class="font-mono">cellSignalChanged</code> updates. On web
              it should report unsupported.
            </p>
          </div>
          <div class="flex items-center gap-2">
            <button
              class="inline-flex items-center justify-center rounded-lg bg-primary-2 px-4 py-2 text-white text-sm font-medium shadow hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-70"
              :disabled="isSignalLoading || isSignalMonitoring"
              @click="startCellSignalMonitoring"
            >
              {{ isSignalLoading ? 'Starting...' : 'Start monitoring' }}
            </button>
            <button
              class="inline-flex items-center justify-center rounded-lg border border-light-2 px-4 py-2 text-sm font-medium shadow-sm hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-70 dark:border-dark-2"
              :disabled="!isSignalMonitoring"
              @click="stopCellSignalMonitoring"
            >
              Stop monitoring
            </button>
          </div>
        </div>

        <div class="mt-4 grid grid-cols-1 gap-3 md:grid-cols-6">
          <div class="rounded-lg border border-light-2 p-3 dark:border-dark-2">
            <div class="text-[11px] uppercase opacity-70">isAvailable</div>
            <div class="mt-1 text-sm font-semibold">
              {{ signalResult?.isAvailable ?? '-' }}
            </div>
          </div>
          <div class="rounded-lg border border-light-2 p-3 dark:border-dark-2">
            <div class="text-[11px] uppercase opacity-70">dBm</div>
            <div class="mt-1 text-sm font-semibold">
              {{ signalResult?.dbm ?? '-' }}
            </div>
          </div>
          <div class="rounded-lg border border-light-2 p-3 dark:border-dark-2">
            <div class="text-[11px] uppercase opacity-70">quality</div>
            <div class="mt-1 text-sm font-semibold">
              {{ signalResult?.quality ?? '-' }}
            </div>
          </div>
          <div class="rounded-lg border border-light-2 p-3 dark:border-dark-2">
            <div class="text-[11px] uppercase opacity-70">reason</div>
            <div class="mt-1 text-sm font-semibold break-all">
              {{ signalResult?.reason ?? '-' }}
            </div>
          </div>
          <div class="rounded-lg border border-light-2 p-3 dark:border-dark-2">
            <div class="text-[11px] uppercase opacity-70">timestamp</div>
            <div class="mt-1 text-sm font-semibold break-all">
              {{ signalTimestampLabel }}
            </div>
          </div>
          <div class="rounded-lg border border-light-2 p-3 dark:border-dark-2">
            <div class="text-[11px] uppercase opacity-70">age</div>
            <div class="mt-1 text-sm font-semibold">
              {{ signalAgeLabel }}
            </div>
          </div>
        </div>

        <p
          v-if="lastSignalCheckedAt"
          class="mt-3 text-[11px] text-neutral-600 dark:text-neutral-300"
        >
          Last update received: {{ lastSignalCheckedAt }}
          <span v-if="isSignalMonitoring">(monitoring active)</span>
        </p>
        <p
          v-if="signalError"
          class="mt-2 text-xs text-light-error dark:text-dark-error"
        >
          {{ signalError }}
        </p>

        <pre
          class="mt-3 max-h-56 overflow-auto rounded-lg border border-light-2 bg-light-1 p-3 text-xs dark:border-dark-2 dark:bg-dark-1"
        ><code>{{ signalResultJson }}</code></pre>
      </div>

      <div
        class="sticky bottom-0 mt-4 md:mt-6 backdrop-blur supports-[backdrop-filter]:bg-light-0/75 supports-[backdrop-filter]:dark:bg-dark-0/75"
      >
        <div
          class="flex items-center justify-between gap-3 rounded-xl border border-light-2 bg-light-0 p-3 md:p-4 shadow-sm dark:border-dark-2 dark:bg-dark-0"
        >
          <div class="text-xs text-neutral-600 dark:text-neutral-300">
            Paste JSON and click start. Errors will be shown under each field.
          </div>
          <div class="flex items-center gap-3">
            <div
              class="hidden sm:inline-flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs text-blue-800 dark:border-blue-400/30 dark:bg-blue-500/10 dark:text-blue-200"
            >
              <span class="font-semibold">Info</span>
              <span>Sandbox: localStorage is enabled; saving is disabled</span>
            </div>
            <button
              class="inline-flex items-center justify-center rounded-lg bg-primary-2 px-4 py-2 text-white text-sm font-medium shadow hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-primary-2/40"
              @click="startOrder"
            >
              Start Order
            </button>
            <span v-if="startMessage" class="ml-1 text-sm opacity-80">{{
              startMessage
            }}</span>
          </div>
        </div>
      </div>
    </div>
  </LayoutWrapper>

  <BaseModal :active="showVersionModal" @closed="closeModal()">
    <h2 class="text-lg font-semibold mb-3">Select Version</h2>
    <table class="w-full text-xs border-collapse">
      <thead class="sticky top-0 bg-light-1 dark:bg-dark-1">
        <tr class="text-left border-b border-light-2 dark:border-dark-2">
          <th class="p-2">Version ID</th>
          <th class="p-2">Is Latest</th>
          <th class="p-2">Last Modified</th>
          <th class="p-2 text-right"></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="version in businessEntityVersions"
          :key="version.versionId"
          class="border-b border-light-2 dark:border-dark-2 hover:bg-light-1 dark:hover:bg-dark-1 transition"
        >
          <td class="p-2 break-all">{{ version.versionId }}</td>
          <td class="p-2">{{ version.isLatest ? '✅' : '❌' }}</td>
          <td class="p-2">
            {{ new Date(version.lastModified).toLocaleString() }}
          </td>
          <td class="p-2 text-right">
            <button
              class="text-xs px-2 py-1 bg-primary-2 text-white rounded-md"
              @click="loadBusinessEntityVersion(version.versionId)"
            >
              Load
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </BaseModal>
</template>

<script lang="ts" setup>
import type { PluginListenerHandle } from '@capacitor/core';
import { computed, onUnmounted, ref, watch } from 'vue';
import LayoutWrapper from '@/components/LayoutWrapper.vue';
import { Order, OrderType } from '@/models/Order';
import { useOrderStore } from '@/store/order/orderStore';
import { plainToInstance } from 'class-transformer';
import { useRouter } from 'vue-router';
import classTransformer from '@/utils/transformers/classTransformer';
import { useEntitiesStore } from '@/store/entities/entitiesStore';
import { useUserStore } from '@/store/user/userStore';
import { useLayoutStore, WorkMode } from '@/store/layout/layoutStore';
import { usePersistenceStore } from '@/store/persistence/persistenceStore';
import {
  getBackupsByPropertyId,
  getBackupVersion,
} from '@/api/businessEntity/BusinessEntityApi';
import BaseModal from '@/components/UI/Modal/BaseModal.vue';
import { CellSignal, type CellSignalResult } from '@/native/plugins/CellSignal';

const router = useRouter();

const advancedInput = ref<string>('');
const orderJsonInput = ref<string>('');
const beJsonError = ref<string>('');
const orderJsonError = ref<string>('');

const orderFileInput = ref<HTMLInputElement | null>(null);
const beFileInput = ref<HTMLInputElement | null>(null);

const orderStore = useOrderStore();
const orderTypeOptions = [
  { value: OrderType.ON_SITE_INSPECTION, label: 'On-Site Inspection (BE)' },
  { value: OrderType.TKD, label: 'TKD' },
  { value: OrderType.MONT, label: 'Installation (MONT)' },
];
const selectedOrderType = ref<OrderType>(OrderType.ON_SITE_INSPECTION);
const startMessage = ref<string>('');

const propertyIdSearch = ref('');
const showVersionModal = ref(false);
const businessEntityVersions = ref<any[]>([]);
const isSignalLoading = ref(false);
const isSignalMonitoring = ref(false);
const signalError = ref('');
const signalResult = ref<CellSignalResult | null>(null);
const lastSignalCheckedAt = ref('');
const signalNow = ref(Date.now());
let signalListenerHandle: PluginListenerHandle | null = null;
let signalAgeIntervalId: ReturnType<typeof setInterval> | null = null;

const signalResultJson = computed(() => {
  if (!signalResult.value) {
    return 'No result yet. Start monitoring to receive updates.';
  }

  return JSON.stringify(signalResult.value, null, 2);
});

const signalTimestampLabel = computed(() => {
  if (!signalResult.value?.timestamp) {
    return '-';
  }

  return new Date(signalResult.value.timestamp).toLocaleString();
});

const signalAgeLabel = computed(() => {
  const timestamp = signalResult.value?.timestamp;

  if (!timestamp) {
    return '-';
  }

  const ageInSeconds = Math.max(
    0,
    Math.floor((signalNow.value - timestamp) / 1000)
  );

  if (ageInSeconds < 60) {
    return `${ageInSeconds}s ago`;
  }

  const minutes = Math.floor(ageInSeconds / 60);
  const seconds = ageInSeconds % 60;
  return `${minutes}m ${seconds}s ago`;
});

const orderSample = {
  id: '2407f6be-1f42-4db1-bab7-da1cad6d6757',
  orderId: '0384097729',
  installerId: undefined,
  installerName: undefined,
  propertyId: undefined,
  businessEntityId: undefined,
  billingUnitReference: undefined,
  workType: undefined,
  status: 'IN_PROGRESS',
  notes: '',
  locationReference: 'C',
  appointment: {
    start: '2024-08-27T06:00:00.000Z',
    end: '2024-08-27T08:00:00.000Z',
  },
  contactPerson: {
    name: 'Heimstaden Hamburg Residential 114 GmbH',
    role: 'CUSTOMER',
    customerId: '001S7000005i8iAIAQ',
  },
  primaryAddress: undefined,
  additionalAddresses: [],
  articles: {
    items: [],
  },
  serviceGroups: [],
};

function triggerFileSelect(type: 'order' | 'be') {
  if (type === 'order') orderFileInput.value?.click();
  else beFileInput.value?.click();
}

function handleFileSelectChange(event: Event, type: 'order' | 'be') {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (file) readJsonFile(file, type);
}

function handleFileDrop(event: DragEvent, type: 'order' | 'be') {
  const file = event.dataTransfer?.files?.[0];
  if (file) readJsonFile(file, type);
}

function readJsonFile(file: File, type: 'order' | 'be') {
  const reader = new FileReader();
  reader.onload = (e) => {
    const content = e.target?.result as string;
    try {
      const parsed = JSON.parse(content);
      const formatted = JSON.stringify(parsed, null, 2);
      if (type === 'order') orderJsonInput.value = formatted;
      else advancedInput.value = formatted;
    } catch {
      if (type === 'order') orderJsonError.value = 'Invalid JSON file';
      else beJsonError.value = 'Invalid JSON file';
    }
  };
  reader.readAsText(file);
}

async function fetchBusinessEntityVersions() {
  beJsonError.value = '';
  if (!propertyIdSearch.value.trim()) {
    beJsonError.value = 'Please enter a Property ID.';
    return;
  }

  try {
    const res = await getBackupsByPropertyId(propertyIdSearch.value);
    if (res.status !== 200) throw new Error('Failed to fetch versions');
    businessEntityVersions.value = res.data.versions;
    showVersionModal.value = true;
  } catch (e) {
    console.error(e);
    beJsonError.value = 'Failed to fetch versions.';
  }
}

async function loadBusinessEntityVersion(version: string) {
  const businessEntity = await getBackupVersion(
    propertyIdSearch.value,
    version
  );
  advancedInput.value = JSON.stringify(businessEntity.data.raw);

  showVersionModal.value = false;
  beJsonError.value = '';
}

function closeModal() {
  showVersionModal.value = false;
}

function ensureSignalAgeTimer() {
  if (signalAgeIntervalId) {
    return;
  }

  signalAgeIntervalId = window.setInterval(() => {
    signalNow.value = Date.now();
  }, 1000);
}

async function stopCellSignalMonitoring() {
  if (signalListenerHandle) {
    await signalListenerHandle.remove();
    signalListenerHandle = null;
  }

  try {
    await CellSignal.stopMonitoring();
  } catch (error) {
    console.error(error);
  } finally {
    isSignalMonitoring.value = false;
  }
}

async function startCellSignalMonitoring() {
  isSignalLoading.value = true;
  signalError.value = '';

  try {
    await stopCellSignalMonitoring();

    signalListenerHandle = await CellSignal.addListener(
      'cellSignalChanged',
      (signal) => {
        signalResult.value = signal;
        lastSignalCheckedAt.value = new Date().toLocaleString();
        signalNow.value = Date.now();
      }
    );

    await CellSignal.startMonitoring();
    isSignalMonitoring.value = true;
    ensureSignalAgeTimer();
  } catch (error) {
    console.error(error);
    await stopCellSignalMonitoring();
    signalError.value = 'Monitoring failed to start.';
  } finally {
    isSignalLoading.value = false;
  }
}

async function startOrder() {
  startMessage.value = '';
  try {
    beJsonError.value = '';
    orderJsonError.value = '';
    // Ensure SANDBOX mode is active so defaults (disabled localStorage/saving) apply

    if (!advancedInput.value.trim()) {
      beJsonError.value = 'Business Entity JSON is required';
      startMessage.value = 'Please fix the JSON errors above.';
      return;
    }

    let businessEntityJson: any;
    try {
      businessEntityJson = JSON.parse(advancedInput.value);
    } catch (e) {
      beJsonError.value = 'Invalid Business Entity JSON';
      startMessage.value = 'Please fix the JSON errors above.';
      return;
    }

    if (useLayoutStore().isLocalStorageEnabled()) {
      usePersistenceStore().saveBusinessEntityToLocal(businessEntityJson);
    }

    const businessEntityMap =
      classTransformer.plainToClassEntity(businessEntityJson);

    let orderPayload: any = orderSample;
    if (orderJsonInput.value?.trim()) {
      try {
        orderPayload = JSON.parse(orderJsonInput.value);
      } catch (e) {
        orderJsonError.value = 'Invalid Order JSON';
        startMessage.value = 'Please fix the JSON errors above.';
        return;
      }
    }
    const order = plainToInstance(Order, orderPayload);
    const userId = useUserStore().userAttributes?.userId;
    order.installerId = userId ? userId : 'Random User ID';

    try {
      const beValues = businessEntityMap.values().next().value;
      order.propertyId = order.propertyId || beValues?.propertyId;
      order.businessEntityId =
        order.businessEntityId || beValues?.id || beValues?.businessEntityId;
      order.billingUnitReference =
        order.billingUnitReference || beValues?.billingUnitReference;
      order.primaryAddress = order.primaryAddress || beValues?.address;
    } catch {
      // Intentionally empty - use defaults if BE values unavailable
    }
    if (!order.workTypeCode) {
      order.workTypeCode = selectedOrderType.value;
    }

    orderStore.setOrders([order]);

    useEntitiesStore().setBusinessEntityList(businessEntityMap);
    useEntitiesStore().setBusinessEntityMap(businessEntityMap);
    useEntitiesStore().setActiveBusinessEntityById(order.businessEntityId);

    const startedOrder = await orderStore.startOrder(order.id);
    if (!startedOrder) {
      console.error('Failed to start order in DevSandbox', {
        orderId: order.id,
      });
      startMessage.value = 'Failed to start order';
      return;
    }

    router.push('/gebaeudeuebersicht');
  } catch (e) {
    console.error(e);
    startMessage.value = 'Failed to start order';
  }
}

watch(advancedInput, () => {
  if (beJsonError.value) beJsonError.value = '';
});
watch(orderJsonInput, () => {
  if (orderJsonError.value) orderJsonError.value = '';
});

onUnmounted(async () => {
  if (signalAgeIntervalId) {
    clearInterval(signalAgeIntervalId);
    signalAgeIntervalId = null;
  }

  await stopCellSignalMonitoring();
});
</script>

<style scoped></style>
